import { Link, useSearchParams } from "react-router-dom";
import { useQuery } from "react-query";
import { publicApi } from "../../utils/util";
import Loading from "component/Loading";
import TopicsNavbar from "component/klass/TopicsNavbar";
import { useKlassCategory, KlassCategoryMobileBar } from "component/klass/KlassCategoryNav";
import CourseCard from "component/klass/CourseCard";
import CoursesSearchForm from "component/klass/CoursesSearchForm";
import CoursesFilterForm from "component/klass/CoursesFilterForm";
import { CourseList } from "types/klass";
import { CLASS_ROUTE } from "@/constant/urlConstant";
import ClassBanner from "component/advertisment/banner";
import { getStaticImgUrl } from "utils/imageUtil";
import { getTopicCategoryBadge, TOPICS, TOPIC_SUB_CATEGORIES } from "@/constants";
import { DR_YOUNG_SEARCH_CHANNEL_ID } from "@/utils/channelCategory";

const DR_YOUNG_GROUP = "drYoungAcademy";

const limit = 200;

const KlassSearch = () => {
  const [searchParams] = useSearchParams();
  const q = searchParams.get("q");
  const topics = searchParams.get("topics");
  const free = searchParams.get("free");
  const level = searchParams.get("level");
  const lecturers = searchParams.get("lecturers");
  const toothNumbers = searchParams.get("toothNumbers");
  const sort = searchParams.get("sort") || "threeMonthlySales:desc";
  const topicCategory = getTopicCategoryBadge(searchParams.get("topicCategory"));
  const topicSubCategory = getTopicCategoryBadge(searchParams.get("topicSubCategory"));
  const channelId =
    searchParams.get("categoryGroup") === DR_YOUNG_GROUP ? DR_YOUNG_SEARCH_CHANNEL_ID : undefined;

  const currentTopic = TOPICS.find((topic) => topic.label.replace(/\s+/g, "") === topics);
  const hasCategoryNav =
    !!currentTopic && (TOPIC_SUB_CATEGORIES[currentTopic.name]?.length ?? 0) > 0;

  const { data: topicCourses } = useQuery<CourseList[] | undefined, Error>(
    ["KlassSearchTopicCourses", { topics, channelId }],
    () => {
      return publicApi
        ?.get("/class/public/courses/search", {
          params: { topics, sort, limit, channelId },
        })
        .then((res) => res.data.results.hits);
    },
    {
      refetchOnWindowFocus: false,
      enabled: hasCategoryNav,
      keepPreviousData: true,
    },
  );

  const categoryNav = useKlassCategory(topicCourses);

  const { data: courses, isLoading: coursesLoading } = useQuery<CourseList[] | undefined, Error>(
    [
      "KlassSearch",
      {
        q,
        topics,
        free,
        level,
        lecturers,
        toothNumbers,
        sort,
        limit,
        topicCategory,
        topicSubCategory,
        channelId,
      },
    ],
    () => {
      return publicApi
        ?.get("/class/public/courses/search", {
          params: {
            q,
            topics,
            free,
            level,
            lecturers,
            toothNumbers,
            sort,
            limit,
            topicCategory,
            topicSubCategory,
            channelId,
          },
        })
        .then((res) => {
          return res.data.results.hits;
        });
    },
    {
      refetchOnWindowFocus: false,
      keepPreviousData: true,
    },
  );

  if (!courses) return <Loading small />;

  const resultsSection = (
    <>
      <div className="my-4 flex flex-wrap items-center gap-y-3 lg:flex-nowrap lg:gap-y-0 lg:my-5">
        <div className="order-1 w-full lg:order-2 lg:w-auto lg:max-w-[330px] lg:grow border border-medi-border-primary rounded-[5px] h-fit">
          <CoursesSearchForm />
        </div>
        <div className="order-2 grow lg:order-1">
          <h3 className="text-secondary-800 text-[16px] lg:text-[18px] font-semibold">
            검색결과 {courses.length}건
          </h3>
        </div>
        <div className="order-3 ml-2 lg:ml-3 flex items-center justify-end gap-2">
          <CoursesFilterForm />
        </div>
      </div>
      {!coursesLoading && courses.length === 0 && (
        <div className="mt-[54px] flex flex-col items-center justify-center">
          <div className="flex flex-col items-center justify-center space-y-4">
            <img src={getStaticImgUrl("klass/order/complete_icon.svg")} alt="" />
            <h3 className="text-xl text-gray-900 font-semibold">검색결과가 없습니다.</h3>
            <div className="text-medi-text-tertiary text-base font-medium text-center">
              다른 검색어를 입력해주세요.
              <br />더 나은 검색결과를 보여드릴 수 있도록 노력하겠습니다!
            </div>
          </div>
        </div>
      )}
      {!coursesLoading && courses.length > 0 && (
        <div className="w-full grid grid-cols-2 gap-x-[12px] gap-y-[24px] lg:grid-cols-4 lg:gap-x-[4px] lg:gap-y-[40px] mb-[100px]">
          {courses.map((item) => {
            return (
              <Link to={`/${CLASS_ROUTE}/course/${item.id}`} key={item.id}>
                <CourseCard
                  thumbnail={item.thumbnailImageUrl}
                  lecturers={item.lecturers}
                  title={item.title}
                  regularPrice={item?.product?.regularPrice}
                  salePrice={item?.product?.salePrice}
                  topic={item?.groupedBadges?.topics}
                  topicCategory={item?.groupedBadges?.topicCategory}
                  level={item?.groupedBadges?.level?.join(",")}
                  isFree={item.isFree}
                />
              </Link>
            );
          })}
        </div>
      )}
    </>
  );

  return (
    <div className="max-w-[1200px] mx-auto">
      <div className="lg:px-4 xl:px-0">
        <div className="mb-1.5 lg:mb-6">
          <div className="hidden lg:block">
            <ClassBanner name="pc_KlassSearch_top" page="KlassSearch" />
          </div>
        </div>
        <TopicsNavbar />
      </div>
      {categoryNav.hasCategoryNav && <KlassCategoryMobileBar nav={categoryNav} />}

      <div className="px-4 xl:px-0">{resultsSection}</div>
    </div>
  );
};

export default KlassSearch;
